import DatePicker from 'antd/es/date-picker';
import Input from 'antd/es/input';
import Select from 'antd/es/select';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import type { FilterConfig, SearchFilter } from '../DataTable/DataTable.types';

const { RangePicker } = DatePicker;

interface FilterInputProps {
  filter: FilterConfig;
}

const StyledSelect = styled(Select)<{ $width?: number }>`
  width: ${({ $width }) => $width || 180}px;
`;

const StyledRangePicker = styled(RangePicker)<{ $width?: number }>`
  width: ${({ $width }) => $width || 280}px;
`;

const NumberRangeContainer = styled.div<{ $width?: number }>`
  display: flex;
  gap: ${({ theme }) => theme.marginXS}px;
  width: ${({ $width }) => $width || 200}px;
`;

const PlaceholderText = styled.span`
  color: ${({ theme }) => theme.colorTextTertiary};
  font-size: ${({ theme }) => theme.fontSizeSM}px;
`;

const StyledSearchInput = styled(Input.Search)<{ $width?: number }>`
  width: ${({ $width }) => $width || 200}px;
`;

export const FilterInput: React.FC<FilterInputProps> = ({ filter }) => {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (filter.type === 'search') {
      setInputValue(filter.value || '');
    }
  }, [filter]);

  if (filter.type === 'select') {
    return (
      <StyledSelect
        placeholder={filter.placeholder || `Select ${filter.label}`}
        $width={filter.width}
        size="small"
        value={filter.value}
        onChange={(value) => filter.onChange(value as string | null)}
        allowClear
      >
        {filter.options.map((option) => (
          <Select.Option key={option.value} value={option.value}>
            {option.label}
          </Select.Option>
        ))}
      </StyledSelect>
    );
  }

  if (filter.type === 'dateRange') {
    return (
      <StyledRangePicker
        $width={filter.width}
        size="small"
        value={
          filter.value
            ? [
                filter.value[0] ? dayjs(filter.value[0]) : null,
                filter.value[1] ? dayjs(filter.value[1]) : null,
              ]
            : null
        }
        onChange={(dates) => {
          if (dates && dates[0] && dates[1]) {
            filter.onChange([dates[0].toISOString(), dates[1].toISOString()]);
          } else {
            filter.onChange(null);
          }
        }}
        format="YYYY-MM-DD"
        allowClear
      />
    );
  }

  if (filter.type === 'search') {
    return (
      <StyledSearchInput
        placeholder={filter.placeholder || `Search ${filter.label}`}
        $width={filter.width}
        size="small"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onSearch={(value) => (filter as SearchFilter).onChange(value)}
        allowClear
      />
    );
  }

  if (filter.type === 'number') {
    return (
      <NumberRangeContainer $width={filter.width}>
        {/* Placeholder for number range input */}
        <PlaceholderText>Number range not implemented</PlaceholderText>
      </NumberRangeContainer>
    );
  }

  if (filter.type === 'boolean') {
    return (
      <StyledSelect
        placeholder={filter.placeholder || filter.label}
        $width={filter.width || 120}
        size="small"
        value={filter.value}
        onChange={(value) => filter.onChange(value as boolean)}
        allowClear
      >
        <Select.Option value={true}>Yes</Select.Option>
        <Select.Option value={false}>No</Select.Option>
      </StyledSelect>
    );
  }

  return null;
};
